import React from 'react'
import { useTranslation } from 'react-i18next'
import { RatingNpsQuestion } from '../types/nps.types'

interface Props {
    question: RatingNpsQuestion
    value: number | undefined
    onChange: (value: number) => void
    error?: string
}

const NpsRating: React.FC<Props> = ({ question, value, onChange, error }) => {
    const { t } = useTranslation()
    const { id, question: label, min, max, minLabel, maxLabel, required } = question

    const values: number[] = []
    for (let n = min; n <= max; n++) values.push(n)

    const groupId = `nps-rating-${id}`
    const errorId = `${groupId}-error`

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-start gap-1">
                <span id={groupId} className="text-[0.75rem] font-semibold text-gray-800">
                    {label}
                </span>
                {required && (
                    <span aria-hidden className="text-red-500">
                        *
                    </span>
                )}
            </div>

            <div
                role="radiogroup"
                aria-labelledby={groupId}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className="flex flex-wrap gap-1.5"
                data-testid={`nps-rating-${id}`}
            >
                {values.map((n) => {
                    const selected = value === n
                    return (
                        <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            aria-label={`${n}`}
                            onClick={() => onChange(n)}
                            className={`min-w-[2rem] h-8 px-2 rounded-md border text-[0.75rem] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                                selected ? 'bg-primary text-secondary border-primary shadow-inner' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                            }`}
                            data-testid={`nps-rating-${id}-option-${n}`}
                        >
                            {n}
                        </button>
                    )
                })}
            </div>

            {(minLabel || maxLabel) && (
                <div className="flex justify-between text-[0.583rem] text-gray-500">
                    <span>{minLabel ? t(minLabel) : ''}</span>
                    <span>{maxLabel ? t(maxLabel) : ''}</span>
                </div>
            )}

            {error && (
                <p id={errorId} role="alert" className="text-error text-[0.583rem]">
                    {error}
                </p>
            )}
        </div>
    )
}

export default NpsRating
